import type { NavItem } from "@/config/navigation";
import { buttonVariants } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";
import { getPremiumUpgradePath } from "@/utils/premiumNavigation";

export function formatNavBadgeCount(badgeCount?: number): string | null {
  if (typeof badgeCount !== "number" || badgeCount <= 0) return null;
  return badgeCount > 99 ? "99+" : String(badgeCount);
}

export function shouldShowProBadge(
  requiresPremium: boolean,
  isPaid: boolean,
  subscriptionLoading: boolean
): boolean {
  return requiresPremium && !isPaid && !subscriptionLoading;
}

export function buildTooltipContent(label: string, showProBadge: boolean): string {
  return showProBadge ? `${label} (Pro)` : label;
}

/** Direct navigation target when the destination is allowed for the current user. */
export function getOpenNavHref(href: string): string {
  return href;
}

/** Upgrade destination when a nav item requires a paid plan. */
export function getPremiumUpgradeNavHref(): string {
  return getPremiumUpgradePath();
}

export interface NavIconClassOptions {
  isMobile: boolean;
  isCollapsed: boolean;
  isActive: boolean;
  targetHref: string;
  href: string;
  isExpanded?: boolean;
}

/**
 * Get Nav Icon Class.
 */
export function getNavIconClass(options: NavIconClassOptions): string {
  const { isMobile, isCollapsed, isActive, targetHref, href, isExpanded } = options;
  const active = isActive && targetHref === href;

  return [
    "flex-shrink-0 transition-colors duration-200",
    isMobile ? "h-5 w-5 mr-3" : "h-4 w-4",
    !isCollapsed && !isMobile ? "mr-3" : "",
    active || isExpanded ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
  ]
    .filter(Boolean)
    .join(" ");
}

export function hasSubmenu(submenu?: NavItem[]): submenu is NavItem[] {
  return Boolean(submenu && submenu.length > 0);
}

export function shouldRenderCollapsedTooltip(isCollapsed: boolean, isMobile: boolean): boolean {
  return isCollapsed && !isMobile;
}

/**
 * Nav Link Class.
 */
export function navLinkClass(options: {
  isActive: boolean;
  isMobile: boolean;
  isCollapsed: boolean;
  isExpanded?: boolean;
  targetHref: string;
  href: string;
}) {
  const { isActive, isMobile, isCollapsed, isExpanded, targetHref, href } = options;
  const active = isActive && targetHref === href;

  return cn(
    buttonVariants({ variant: "ghost" }),
    "w-full justify-start font-medium transition-all duration-200 rounded-lg group animate-fade-in",
    isMobile ? "h-10 px-4 text-sm" : "h-9 px-3 text-sm",
    active || isExpanded
      ? "bg-muted text-orange-950 hover:bg-muted/80 shadow-sm"
      : "text-foreground/70 hover:bg-muted/50 hover:text-foreground",
    isCollapsed && !isMobile && "px-2 justify-center"
  );
}
